import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { FilterField, filterControlClass } from '@/components/admin/versioned/filter-field';
import {
  REPLAY_FILTER_HELP,
  REPLAY_FILTER_OPTION_LABEL,
  isReplayFilter,
  replayFilterChipLabel,
} from '@/lib/replay-mark';
import {
  REVIEW_FILTER_OPTION_LABEL,
  type ReviewFilter,
  reviewFilterChipLabel,
} from '@/lib/review-entry';
import { sessionStatusLabel } from '@/lib/session-labels';
import { isSessionOrigin, originFilterChipLabel } from '@/lib/session-origin';
import type {
  NoyauCode,
  ReplayFilter,
  SessionOrigin,
  SessionStatus,
} from '@matter_extractor/shared';
import { NOYAUX, isNoyauCode, noyauOf, sessionStatusSchema } from '@matter_extractor/shared';
import { SlidersHorizontal, X } from 'lucide-react';
import { useId } from 'react';

/**
 * Les dix filtres de §12.1, au régime des bibliothèques de §4 : quatre
 * visibles, six derrière « Plus de filtres », des pastilles retirables et un
 * compte au-dessus du tableau. Un onzième depuis #192, « Origine » (les
 * sessions remontées de la prod, ou celles nées ici), VISIBLE avec les quatre
 * premiers : la revue de la maquette `atelier.html` l'a voulu en tête, pas
 * derrière « Plus de filtres ».
 *
 * ## Quatre et six, et pas dix
 *
 * « Sept sélecteurs en ligne ne tiennent pas » (§12.1, et la maquette
 * `liste.html` le dessine). Les quatre visibles - expérience, statut, lien,
 * période - sont ceux qu'on arme sans y penser ; les six cachés - protocole,
 * persona, critère, depuis #152 le noyau RETENU (noyau.md §9.2), depuis #187 la
 * relecture et depuis #193 le rejeu - répondent à une question avec laquelle on
 * est arrivé. Le bouton porte le COMPTE des six, sans quoi un filtre armé
 * derrière un panneau fermé est une liste qu'on ne comprend plus.
 *
 * ## « Relecture : avec / sans », le neuvième (relecture.md §13)
 *
 * Il retient les sessions dont la relecture porte au moins un élément, ou aucun.
 * Il ne dit rien du noyau du relecteur, qui n'entre pas dans la liste : la
 * colonne Noyau reste celle de la machine.
 *
 * ## « Rejeu : avec / sans », le dixième (rejeu.md §12)
 *
 * Au patron exact de la relecture : « Toutes », « Les rejeux seulement », « Sans
 * les rejeux ». Il retient les sessions que Claude a jouées à la place d'une
 * personne, ou celles qu'une personne a jouées ; sa phrase d'aide le dit sous le
 * panneau, et sa pastille s'appelle « Rejeu : avec » ou « Rejeu : sans ».
 *
 * ## Le noyau retenu offre les six, et rien d'autre
 *
 * Les six noyaux de la table de §2.1, dans son ordre, et pas un septième
 * « sans noyau » (N26) : une session sans noyau retenu n'en satisfait aucun, et
 * le panneau le DIT - on filtre pour RETROUVER les sessions d'un noyau, pas pour
 * classer un corpus (§9.2), donc le tri ne bouge pas.
 *
 * ## Les trois cachés portent sur l'OBJET et jamais sur une version
 *
 * « Une session fige les versions qu'elle a jouées, la liste les résout vers le
 * protocole, le persona et les critères d'aujourd'hui » (§12.1). Le panneau le
 * DIT, parce que c'est contre-intuitif : on filtre sur le protocole qu'on
 * connaît, et on obtient toutes les sessions qui en ont joué une version
 * quelconque. Et « critère » retient les sessions dont le protocole figé cite ce
 * critère.
 *
 * ## Il ne décide de rien
 *
 * Contrôlé de bout en bout : les valeurs arrivent, les changements repartent en
 * `patch`, et c'est l'écran qui écrit l'URL. Ce fichier ne sait donc ni d'où
 * viennent les options ni où va le tri - il n'y en a pas, §12.1 fixant l'ordre.
 */

/* ------------------------------------------------------------- les valeurs */

/**
 * L'état des dix filtres, `null` valant « pas armé ».
 *
 * `null` et pas `undefined` : l'écran doit pouvoir dire « retire celui-ci »,
 * et sous `exactOptionalPropertyTypes` une propriété optionnelle refuse un
 * `undefined` explicite. `null` est l'orthographe que ce dépôt donne à
 * « délibérément absent ».
 */
export interface SessionFilterState {
  readonly experienceId: string | null;
  readonly status: SessionStatus | null;
  readonly linkId: string | null;
  /** La période, en jours `YYYY-MM-DD`. Les deux bornes sont INCLUSES (§12.1). */
  readonly from: string | null;
  readonly to: string | null;
  readonly protocolId: string | null;
  readonly personaId: string | null;
  readonly criterionId: string | null;
  /** Le noyau RETENU au premier choix (noyau.md §9.2), par son triplet. */
  readonly noyau: NoyauCode | null;
  /** La relecture (relecture.md §13) : les sessions qui en portent une, ou aucune. */
  readonly review: ReviewFilter | null;
  /** Le rejeu (rejeu.md §12) : les sessions que Claude a jouées, ou celles d'une personne. */
  readonly replay: ReplayFilter | null;
  /**
   * L'origine (vitrine.md §8.2, #192) : les sessions remontées de la prod, ou
   * celles nées ici. Visible parmi les filtres de tête, pas derrière « Plus de
   * filtres » (revue du 2026-10-14).
   */
  readonly origin: SessionOrigin | null;
}

export type SessionFilterPatch = Partial<SessionFilterState>;

export const SESSION_FILTERS_EMPTY: SessionFilterState = {
  experienceId: null,
  status: null,
  linkId: null,
  from: null,
  to: null,
  protocolId: null,
  personaId: null,
  criterionId: null,
  noyau: null,
  review: null,
  replay: null,
  origin: null,
};

/** Un choix de sélecteur : l'identifiant qu'on poste, le nom qu'on lit. */
export interface SessionFilterOption {
  readonly id: string;
  readonly name: string;
}

/** Ce que les sélecteurs offrent, lu sur les lignes plutôt que sur les bibliothèques. */
export interface SessionFilterOptions {
  readonly experiences: readonly SessionFilterOption[];
  readonly links: readonly SessionFilterOption[];
  readonly protocols: readonly SessionFilterOption[];
  readonly personas: readonly SessionFilterOption[];
  readonly criteria: readonly SessionFilterOption[];
}

export const SESSION_OPTIONS_EMPTY: SessionFilterOptions = {
  experiences: [],
  links: [],
  protocols: [],
  personas: [],
  criteria: [],
};

/* --------------------------------------------------------------- les dates */

const MONTHS_SHORT = [
  'janv.',
  'févr.',
  'mars',
  'avril',
  'mai',
  'juin',
  'juill.',
  'août',
  'sept.',
  'oct.',
  'nov.',
  'déc.',
] as const;

/**
 * `1 sept. 2026`, écrit à la main depuis un jour `YYYY-MM-DD`.
 *
 * À la main et pas par `Intl`, le régime de `lib/session-labels.ts` : `Intl`
 * répond selon la locale de la MACHINE, donc un test et une capture ne diraient
 * pas la même chose sur deux runners. Et découpé sur la chaîne plutôt que passé
 * par `Date`, parce qu'un `YYYY-MM-DD` est lu en UTC et rendu en local - un
 * décalage d'un jour sur la moitié de l'année.
 */
export function dayLabel(day: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (match === null) return day;
  const [, year, month, date] = match;
  const name = MONTHS_SHORT[Number(month) - 1];
  if (name === undefined || year === undefined || date === undefined) return day;
  return `${Number(date)} ${name} ${year}`;
}

/** « du 1 sept. 2026 au 14 sept. 2026 », ou la moitié qui est armée. */
export function periodLabel(from: string | null, to: string | null): string | null {
  if (from !== null && to !== null) return `du ${dayLabel(from)} au ${dayLabel(to)}`;
  if (from !== null) return `depuis le ${dayLabel(from)}`;
  if (to !== null) return `jusqu’au ${dayLabel(to)}`;
  return null;
}

/* ------------------------------------------------------------ les pastilles */

export interface SessionFilterChip {
  /** Clé stable, et ce qu'un test vise. Jamais le libellé : les libellés sont français. */
  readonly id: string;
  readonly label: string;
  /** Ce qui neutralise CETTE dimension - la pastille sait se retirer elle-même. */
  readonly patch: SessionFilterPatch;
}

/** Le nom d'un identifiant dans une liste d'options, ou l'identifiant s'il a disparu. */
function nameOf(options: readonly SessionFilterOption[], id: string): string {
  return options.find((one) => one.id === id)?.name ?? id;
}

/**
 * Ce qui est armé, en pastilles, dans l'ordre des sélecteurs.
 *
 * Chaque pastille porte le `patch` qui l'enlève, donc l'écran n'a aucune
 * dimension à connaître : c'est l'arrangement de `activeVersionedFilters`, et la
 * dérive qu'il empêche est une pastille qui s'affiche et ne se retire pas -
 * une impasse dont on ne sort qu'en rechargeant la page.
 */
export function activeSessionChips(
  filters: SessionFilterState,
  options: SessionFilterOptions,
): readonly SessionFilterChip[] {
  const chips: SessionFilterChip[] = [];
  if (filters.experienceId !== null) {
    chips.push({
      id: 'experience',
      label: nameOf(options.experiences, filters.experienceId),
      patch: { experienceId: null },
    });
  }
  if (filters.status !== null) {
    chips.push({
      id: 'statut',
      label: sessionStatusLabel(filters.status),
      patch: { status: null },
    });
  }
  if (filters.linkId !== null) {
    chips.push({
      id: 'lien',
      label: nameOf(options.links, filters.linkId),
      patch: { linkId: null },
    });
  }
  if (filters.origin !== null) {
    chips.push({
      id: 'origine',
      label: originFilterChipLabel(filters.origin),
      patch: { origin: null },
    });
  }
  const period = periodLabel(filters.from, filters.to);
  if (period !== null) {
    // UNE pastille pour les deux bornes : « du 1 au 14 » est une période, et
    // deux pastilles laisseraient retirer une moitié de ce qui se lit d'un bloc.
    chips.push({ id: 'periode', label: period, patch: { from: null, to: null } });
  }
  if (filters.protocolId !== null) {
    chips.push({
      id: 'protocole',
      label: nameOf(options.protocols, filters.protocolId),
      patch: { protocolId: null },
    });
  }
  if (filters.personaId !== null) {
    chips.push({
      id: 'persona',
      label: nameOf(options.personas, filters.personaId),
      patch: { personaId: null },
    });
  }
  if (filters.criterionId !== null) {
    chips.push({
      id: 'critere',
      label: nameOf(options.criteria, filters.criterionId),
      patch: { criterionId: null },
    });
  }
  if (filters.noyau !== null) {
    // Le filtre se NOMME, lui (maquette `SessionListe`) : le nom d'un noyau
    // seul, « Souverain Magicien », se lirait comme un nom de personne.
    chips.push({
      id: 'noyau',
      label: `Noyau retenu : ${noyauOf(filters.noyau).name}`,
      patch: { noyau: null },
    });
  }
  if (filters.review !== null) {
    chips.push({
      id: 'relecture',
      label: reviewFilterChipLabel(filters.review),
      patch: { review: null },
    });
  }
  if (filters.replay !== null) {
    chips.push({
      id: 'rejeu',
      label: replayFilterChipLabel(filters.replay),
      patch: { replay: null },
    });
  }
  return chips;
}

/** Combien des six filtres cachés sont armés - le compte que porte le bouton. */
export function hiddenSessionFilterCount(filters: SessionFilterState): number {
  return [
    filters.protocolId,
    filters.personaId,
    filters.criterionId,
    filters.noyau,
    filters.review,
    filters.replay,
  ].filter((one) => one !== null).length;
}

/** Ce que le sélecteur de la relecture rend : « avec », « sans », ou « pas armé ». */
function reviewOfOption(value: string): ReviewFilter | null {
  return value === 'with' || value === 'without' ? value : null;
}

/** Ce que le sélecteur du rejeu rend : « avec », « sans », ou « pas armé ». */
function replayOfOption(value: string): ReplayFilter | null {
  return isReplayFilter(value) ? value : null;
}

/** Ce que le sélecteur de l'origine rend : « prod », « dev », ou « pas armé ». */
function originOfOption(value: string): SessionOrigin | null {
  return isSessionOrigin(value) ? value : null;
}

/** Ce que le sélecteur du noyau rend : un triplet de la table, ou « pas armé ». */
function noyauOfOption(value: string): NoyauCode | null {
  return isNoyauCode(value) ? value : null;
}

/* ---------------------------------------------------------------- la barre */

export interface SessionFiltersProps {
  readonly filters: SessionFilterState;
  readonly options: SessionFilterOptions;
  readonly expanded: boolean;
  readonly onToggleExpanded: () => void;
  readonly onChange: (patch: SessionFilterPatch) => void;
}

/** Ce que le `<select>` rend quand personne n'a choisi : « pas armé ». */
const NONE = '';
const pick = (value: string): string | null => (value === NONE ? null : value);

export function SessionFilters({
  filters,
  options,
  expanded,
  onToggleExpanded,
  onChange,
}: SessionFiltersProps) {
  const experienceId = useId();
  const statusId = useId();
  const linkId = useId();
  const fromId = useId();
  const toId = useId();
  const protocolId = useId();
  const personaId = useId();
  const criterionId = useId();
  const noyauId = useId();
  const reviewId = useId();
  const replayId = useId();
  const originId = useId();
  const hidden = hiddenSessionFilterCount(filters);

  return (
    <div className="flex flex-col gap-3">
      <div data-testid={SESSIONS_TEST_IDS.filters} className="flex flex-wrap items-end gap-2.5">
        <FilterField label="Expérience" htmlFor={experienceId} className="w-55">
          <select
            id={experienceId}
            data-testid="sessions-filtre-experience"
            value={filters.experienceId ?? NONE}
            onChange={(event) => onChange({ experienceId: pick(event.currentTarget.value) })}
            className={filterControlClass(filters.experienceId !== null, 'w-full')}
          >
            <option value={NONE}>Toutes</option>
            {options.experiences.map((one) => (
              <option key={one.id} value={one.id}>
                {one.name}
              </option>
            ))}
          </select>
        </FilterField>

        <FilterField label="Statut" htmlFor={statusId} className="w-40">
          <select
            id={statusId}
            data-testid="sessions-filtre-statut"
            value={filters.status ?? NONE}
            onChange={(event) =>
              onChange({ status: pick(event.currentTarget.value) as SessionStatus | null })
            }
            className={filterControlClass(filters.status !== null, 'w-full')}
          >
            <option value={NONE}>Tous</option>
            {sessionStatusSchema.options.map((status) => (
              <option key={status} value={status}>
                {sessionStatusLabel(status)}
              </option>
            ))}
          </select>
        </FilterField>

        <FilterField label="Lien" htmlFor={linkId} className="w-45">
          <select
            id={linkId}
            data-testid="sessions-filtre-lien"
            value={filters.linkId ?? NONE}
            onChange={(event) => onChange({ linkId: pick(event.currentTarget.value) })}
            className={filterControlClass(filters.linkId !== null, 'w-full')}
          >
            <option value={NONE}>Tous</option>
            {options.links.map((one) => (
              <option key={one.id} value={one.id}>
                {one.name}
              </option>
            ))}
          </select>
        </FilterField>

        <FilterField label="Origine" htmlFor={originId} className="w-35">
          <select
            id={originId}
            data-testid={SESSIONS_TEST_IDS.originFilter}
            value={filters.origin ?? NONE}
            onChange={(event) => onChange({ origin: originOfOption(event.currentTarget.value) })}
            className={filterControlClass(filters.origin !== null, 'w-full')}
          >
            <option value={NONE}>Toutes</option>
            <option value="prod">prod</option>
            <option value="dev">dev</option>
          </select>
        </FilterField>

        {/*
          La période en DEUX dates et pas un intervalle : la maquette dessine
          deux champs et un « au » entre eux, et c'est aussi ce que le serveur
          prend - deux jours, la borne haute incluse.
        */}
        <div className="flex max-w-full flex-col gap-1">
          <span className="font-semibold text-gray-600 text-xs uppercase tracking-wide">
            Période
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor={fromId} className="sr-only">
              Début de la période
            </label>
            <input
              id={fromId}
              data-testid="sessions-filtre-du"
              type="date"
              value={filters.from ?? NONE}
              onChange={(event) => onChange({ from: pick(event.currentTarget.value) })}
              className={filterControlClass(filters.from !== null, 'w-35')}
            />
            <span aria-hidden="true" className="text-gray-500 text-sm">
              au
            </span>
            <label htmlFor={toId} className="sr-only">
              Fin de la période
            </label>
            <input
              id={toId}
              data-testid="sessions-filtre-au"
              type="date"
              value={filters.to ?? NONE}
              onChange={(event) => onChange({ to: pick(event.currentTarget.value) })}
              className={filterControlClass(filters.to !== null, 'w-35')}
            />
          </div>
        </div>

        {/*
          Poussé au BORD DROIT, comme sur la barre des bibliothèques : ce n'est
          pas un cinquième filtre, c'est la limite de la rangée. À côté de
          « Période » il se lirait comme une chose de plus à choisir ; au bout,
          il se lit « il y en a d'autres ».
        */}
        <button
          type="button"
          data-testid="sessions-plus-de-filtres"
          aria-expanded={expanded}
          onClick={onToggleExpanded}
          className={`ml-auto inline-flex h-9.5 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border-2 border-bleu-nuit px-3 font-semibold text-bleu-nuit text-sm leading-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${expanded ? 'bg-bleu-nuit-tint' : 'bg-white hover:bg-bleu-nuit-tint'}`}
        >
          <SlidersHorizontal aria-hidden="true" className="size-4" />
          <span>{expanded ? 'Moins de filtres' : 'Plus de filtres'}</span>
          {hidden === 0 ? null : (
            <span
              data-testid="sessions-plus-de-filtres-compte"
              className="inline-flex h-4.5 min-w-4.5 items-center justify-center rounded-pill bg-bleu-nuit px-1.5 font-semibold text-caption text-white"
            >
              {hidden}
            </span>
          )}
        </button>
      </div>

      {expanded ? (
        <div
          data-testid={SESSIONS_TEST_IDS.moreFilters}
          className="flex flex-col gap-3 rounded-md border border-gray-100 bg-gray-50 p-4"
        >
          <div className="flex flex-wrap items-end gap-6">
            <FilterField label="Protocole" htmlFor={protocolId} className="w-55">
              <select
                id={protocolId}
                data-testid="sessions-filtre-protocole"
                value={filters.protocolId ?? NONE}
                onChange={(event) => onChange({ protocolId: pick(event.currentTarget.value) })}
                className={filterControlClass(filters.protocolId !== null, 'w-full')}
              >
                <option value={NONE}>Tous</option>
                {options.protocols.map((one) => (
                  <option key={one.id} value={one.id}>
                    {one.name}
                  </option>
                ))}
              </select>
            </FilterField>

            <FilterField label="Persona" htmlFor={personaId} className="w-50">
              <select
                id={personaId}
                data-testid="sessions-filtre-persona"
                value={filters.personaId ?? NONE}
                onChange={(event) => onChange({ personaId: pick(event.currentTarget.value) })}
                className={filterControlClass(filters.personaId !== null, 'w-full')}
              >
                <option value={NONE}>Tous</option>
                {options.personas.map((one) => (
                  <option key={one.id} value={one.id}>
                    {one.name}
                  </option>
                ))}
              </select>
            </FilterField>

            <FilterField label="Critère" htmlFor={criterionId} className="w-50">
              <select
                id={criterionId}
                data-testid="sessions-filtre-critere"
                value={filters.criterionId ?? NONE}
                onChange={(event) => onChange({ criterionId: pick(event.currentTarget.value) })}
                className={filterControlClass(filters.criterionId !== null, 'w-full')}
              >
                <option value={NONE}>Tous</option>
                {options.criteria.map((one) => (
                  <option key={one.id} value={one.id}>
                    {one.name}
                  </option>
                ))}
              </select>
            </FilterField>

            <FilterField label="Noyau retenu" htmlFor={noyauId} className="w-55">
              <select
                id={noyauId}
                data-testid="sessions-filtre-noyau"
                value={filters.noyau ?? NONE}
                onChange={(event) => onChange({ noyau: noyauOfOption(event.currentTarget.value) })}
                className={filterControlClass(filters.noyau !== null, 'w-full')}
              >
                <option value={NONE}>Tous</option>
                {NOYAUX.map((one) => (
                  <option key={one.code} value={one.code}>
                    {one.name}
                  </option>
                ))}
              </select>
            </FilterField>

            <FilterField label="Relecture" htmlFor={reviewId} className="w-50">
              <select
                id={reviewId}
                data-testid="sessions-filtre-relecture"
                value={filters.review ?? NONE}
                onChange={(event) =>
                  onChange({ review: reviewOfOption(event.currentTarget.value) })
                }
                className={filterControlClass(filters.review !== null, 'w-full')}
              >
                <option value={NONE}>Toutes</option>
                <option value="with">{REVIEW_FILTER_OPTION_LABEL.with}</option>
                <option value="without">{REVIEW_FILTER_OPTION_LABEL.without}</option>
              </select>
            </FilterField>

            <FilterField label="Rejeu" htmlFor={replayId} className="w-50">
              <select
                id={replayId}
                data-testid={SESSIONS_TEST_IDS.replayFilter}
                value={filters.replay ?? NONE}
                onChange={(event) =>
                  onChange({ replay: replayOfOption(event.currentTarget.value) })
                }
                className={filterControlClass(filters.replay !== null, 'w-full')}
              >
                <option value={NONE}>Toutes</option>
                <option value="with">{REPLAY_FILTER_OPTION_LABEL.with}</option>
                <option value="without">{REPLAY_FILTER_OPTION_LABEL.without}</option>
              </select>
            </FilterField>
          </div>
          <p className="text-gray-600 text-xs leading-4">
            Protocole, persona et critère portent sur l’<strong>objet</strong> et non sur une
            version : une session fige les versions qu’elle a jouées, la liste les résout vers le
            protocole, le persona et les critères d’aujourd’hui. « Critère » retient les sessions
            dont le protocole figé cite ce critère.
          </p>
          <p className="text-gray-600 text-xs leading-4">
            Une session sans noyau retenu (en cours, sautée faute de détermination, ou sur une
            expérience sans bloc Noyau) ne satisfait aucun choix de « Noyau retenu ».
          </p>
          <p className="text-gray-600 text-xs leading-4">
            « Relecture » retient les sessions dont la relecture porte au moins un élément, posé par
            n’importe qui de l’équipe : le noyau du relecteur n’entre pas dans la liste.
          </p>
          <p
            data-testid={SESSIONS_TEST_IDS.replayFilterHelp}
            className="text-gray-600 text-xs leading-4"
          >
            {REPLAY_FILTER_HELP}
          </p>
        </div>
      ) : null}
    </div>
  );
}

/* --------------------------------------------------------------- le résumé */

export interface SessionFilterSummaryProps {
  readonly chips: readonly SessionFilterChip[];
  /** Ce que la liste montre, et ce qu'elle montrerait sans filtre. */
  readonly shown: number;
  readonly total: number;
  /** Le total en euros des lignes affichées, déjà mis en mots. */
  readonly cost: string;
  /** Combien dorment à la corbeille - le compte que « Afficher la corbeille » porte. */
  readonly trashCount: number;
  readonly trashOpen: boolean;
  readonly onToggleTrash: () => void;
  readonly onChange: (patch: SessionFilterPatch) => void;
}

/**
 * Les pastilles, le compte, et la bascule de la corbeille - une seule rangée.
 *
 * Elles vont ensemble : un compte sans les filtres qui l'ont produit est un
 * nombre que personne ne peut mettre en doute, et un filtre qu'on peut armer
 * sans voir ce qu'il coûte est un filtre qu'on oublie d'avoir armé.
 *
 * « Afficher la corbeille » REMPLACE la liste (§12.4), elle ne s'y ajoute pas :
 * le bouton dit donc « Afficher » ou « Masquer » et jamais « Inclure ». Il ne se
 * dessine pas sur une corbeille vide et une liste vivante - un tiroir qu'on
 * ouvre sur rien n'apprend rien - mais il reste là dès qu'on est DEDANS, sans
 * quoi on n'en sortirait plus.
 */
export function SessionFilterSummary({
  chips,
  shown,
  total,
  cost,
  trashCount,
  trashOpen,
  onToggleTrash,
  onChange,
}: SessionFilterSummaryProps) {
  const plural = shown > 1 ? 's' : '';
  const count = trashOpen
    ? `${shown} session${plural} à la corbeille`
    : `${shown} session${plural} sur ${total} · ${cost} au total`;

  return (
    <div className="flex flex-wrap items-center justify-between gap-4">
      {chips.length === 0 ? (
        <p className="text-gray-500 text-sm">Aucun filtre actif</p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          {chips.map((chip) => (
            <button
              key={chip.id}
              type="button"
              data-testid={SESSIONS_TEST_IDS.filterChip}
              data-chip={chip.id}
              /*
                Le nom accessible dit le GESTE et répète le libellé : le texte
                visible seul se lit comme un constat, pas comme quelque chose
                qu'on peut décrocher.
              */
              aria-label={`Retirer le filtre ${chip.label}`}
              onClick={() => onChange(chip.patch)}
              className="inline-flex cursor-pointer items-center gap-1.5 rounded-pill bg-bleu-nuit-tint py-1 pr-2 pl-2.5 font-semibold text-bleu-nuit text-caption hover:bg-bleu-nuit hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
            >
              <span>{chip.label}</span>
              <X aria-hidden="true" className="size-3.5" strokeWidth={2.5} />
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-4">
        <p data-testid="sessions-compte" className="text-gray-600 text-sm">
          {count}
        </p>
        {trashCount === 0 && !trashOpen ? null : (
          <button
            type="button"
            data-testid={SESSIONS_TEST_IDS.trashToggle}
            aria-pressed={trashOpen}
            onClick={onToggleTrash}
            className="cursor-pointer whitespace-nowrap font-semibold text-bleu-nuit text-sm underline underline-offset-2 hover:text-bleu-nuit-deep"
          >
            {trashOpen ? 'Masquer la corbeille' : `Afficher la corbeille (${trashCount})`}
          </button>
        )}
      </div>
    </div>
  );
}
